import difference from 'lodash/difference';
import { TEAM_MANAGEMENT_EVENT_PREFIX, trackMixpanelEvent } from '../utils/general';
import { IN_OPERATOR, NOT_IN_OPERATOR } from '../utils/reportsConsts';
import { sortAlphabeticallyLowecase, sortDate } from '../utils/sortUtil';

export const translationPrefix = 'STATIC.PAGES.TEAM_MEMBERS';

export const MEMBER_GROUP_TYPES = {
    active: 'active',
    pending: 'pending',
    inactive: 'inactive',
};

export const DELETABLE_GROUPS = [MEMBER_GROUP_TYPES.pending, MEMBER_GROUP_TYPES.active];

export const ROLE_ORG_ADMIN = 0;
export const ROLE_STANDARD = 1;
export const ROLE_RESTRICTED = 2;
export const ROLE_AGENCY = 4;

export const ACCOUNT_PARTNER = 5;
export const ACCOUNT_AGENCY = 8;
export const RECAPTCHA_PUBLIC_KEY = '6LeGkr8ZAAAAAIOFqgfHM15x_VgW2U9Tdl25AuTs';

export const RESTRICTED_ROLES = [ROLE_RESTRICTED, ROLE_AGENCY];

export const CUSTOM_ACCESS_TYPE = 'Custom';

export const RoleTypes = {
    ADMIN: 'Admin',
    STANDARD: 'Standard',
    RESTRICTED: 'Restricted',
    AGENCY: 'Agency',
    PARTNER: 'Partner',
};

export const ACCOUNT_TYPE_NAME_MAP = {
    [ROLE_RESTRICTED]: RoleTypes.RESTRICTED,
    [ACCOUNT_AGENCY]: RoleTypes.AGENCY,
    [ACCOUNT_PARTNER]: RoleTypes.PARTNER,
};

export const ROLES = [
    {
        label: RoleTypes.ADMIN,
        id: ROLE_ORG_ADMIN,
        descriptionKey: `${translationPrefix}.ROLES.ADMIN.DESCRIPTION`,
        default: true,
    },
    {
        label: RoleTypes.STANDARD,
        id: ROLE_STANDARD,
        descriptionKey: `${translationPrefix}.ROLES.STANDARD.DESCRIPTION`,
    },
    {
        label: RoleTypes.RESTRICTED,
        id: ROLE_RESTRICTED,
        descriptionKey: `${translationPrefix}.ROLES.RESTRICTED.DESCRIPTION`,
    },
];

export const getTranslatedItemType = (translate, itemType, data = {}) =>
    translate(`${translationPrefix}.ITEM_TYPES.${itemType.toUpperCase()}`, data);

export const EXCLUDED_METRICS_SELECTION_PAGE = ['unified_data_type', 'installs_metric_group', 'revenue_metric_group'];

export const INITIAL_FIELD_TYPES = [
    {
        type: 'metrics',
        label: 'STATIC.METRICS',
    },
    {
        type: 'discrepancyMetrics',
        label: 'STATIC.SOURCE_METRICS',
    },
    {
        type: 'cohortMetrics',
        label: 'STATIC.COHORT_METRICS',
    },
    {
        type: 'adMonDAUMetrics',
        label: 'STATIC.DAU_METRICS',
    },
    {
        type: 'dataTypeFields',
        label: 'STATIC.DATA_TYPE_FIELDS',
    },
    {
        type: 'metricGroupFields',
        label: 'STATIC.METRIC_GROUP_FIELDS',
    },
    {
        type: 'cohortMetricGroupFields',
        label: 'STATIC.COHORT_METRIC_GROUP_FIELDS',
    },
    {
        type: 'skanModeledMetrics',
        label: 'STATIC.MODELED_SKAN_METRICS',
    },
    {
        type: 'skanModeledCohortMetrics',
        label: 'STATIC.MODELED_SKAN_COHORT_METRICS',
    },
    {
        type: 'skanModeledCustomEvents',
        label: 'STATIC.MODELED_SKAN_CUSTOM_EVENTS',
    },
    {
        type: 'videoMetrics',
        label: 'STATIC.VIDEO_METRICS',
    },
    {
        type: 'socialEngagementMetrics',
        label: 'STATIC.SOCIAL_ENGAGEMENT_METRICS',
    },
];

export const MANAGE_PERMISSIONS_STEPS = {
    roleSelection: 'roleSelection',
    userDetails: 'userDetails',
    apps: 'apps',
    sources: 'sources',
    attribution: 'attribution',
    links: 'links',
    metrics: 'metrics',
    screens: 'screens',
    review: 'review',
};

export const SECTIONS_STEPS = [MANAGE_PERMISSIONS_STEPS.attribution, MANAGE_PERMISSIONS_STEPS.links];

export const MEMBER_TYPES = {
    USER: 'TEAM_USERS',
    AGENCY: 'AGENCIES',
};

export const DIMENSION_STEPS = [MANAGE_PERMISSIONS_STEPS.apps, MANAGE_PERMISSIONS_STEPS.sources];

export const STEPS_BY_MEMBER_TYPE = {
    [MEMBER_TYPES.USER]: {
        baseSteps: [MANAGE_PERMISSIONS_STEPS.roleSelection, MANAGE_PERMISSIONS_STEPS.userDetails],
        finalSteps: [
            MANAGE_PERMISSIONS_STEPS.metrics,
            MANAGE_PERMISSIONS_STEPS.screens,
            MANAGE_PERMISSIONS_STEPS.review,
        ],
    },
    [MEMBER_TYPES.AGENCY]: {
        baseSteps: [],
        finalSteps: [
            MANAGE_PERMISSIONS_STEPS.attribution,
            MANAGE_PERMISSIONS_STEPS.links,
            MANAGE_PERMISSIONS_STEPS.metrics,
            MANAGE_PERMISSIONS_STEPS.screens,
            MANAGE_PERMISSIONS_STEPS.review,
        ],
    },
};

export const PAGE_MODES = {
    create: 'create',
    edit: 'edit',
};

export const DEFAULT_AGENCY_SCREEN_SELECTION = ['reports', 'pivot'];

export const ORGANIC_SOURCE_NAME = 'organic';

export const ALWAYS_DISABLED_SCREENS = ['data-destinations'];
export const HIDDEN_SCREENS = ['team_management'];
export const SCREENS_REQUIRING_ALL_METRICS = ['export_logs'];

const UNKNOWN = 'ZZ';
const TRACKER_UNKNOWN = '$T';
const ADNET_UNKNOWN = '$A';
const ADNET_WHOLE_WORLD = '**';

export const SPECIAL_COUNTRIES = [UNKNOWN, TRACKER_UNKNOWN, ADNET_UNKNOWN, ADNET_WHOLE_WORLD];

export const trackTeamManagementMixpanelEvent = (eventName, params = null) => {
    trackMixpanelEvent(TEAM_MANAGEMENT_EVENT_PREFIX, eventName, params || {});
};

export const getRoleDisplayName = role => {
    const roleLabel = ROLES.find(r => r.id === role);
    return roleLabel ? roleLabel.label : role;
};

export const getUserRole = member => {
    if (member.role) return getRoleDisplayName(member.role.type);
    if (member.is_admin) return RoleTypes.ADMIN;
    if (member.is_restricted) return RoleTypes.RESTRICTED;

    return RoleTypes.STANDARD;
};

export const getUserDisplayName = (user, fieldMap = {}) => {
    const firstName = user.first_name || user[fieldMap.first_name];
    const lastName = user.last_name || user[fieldMap.last_name];
    const email = user.email || user[fieldMap.email];
    return firstName && lastName ? `${firstName} ${lastName}` : email;
};

const getStateFromUserDetailsOld = user => {
    let role = ROLE_STANDARD;
    if (user.is_admin) {
        role = ROLE_ORG_ADMIN;
    } else if (user.is_restricted) {
        role = ROLE_RESTRICTED;
    }
    return {
        roleSelectionForm: {
            role,
        },
        userRegistrationForm: {
            firstName: {
                value: user.first_name,
            },
            lastName: {
                value: user.last_name,
            },
            email: {
                value: user.email,
            },
            description: {
                value: '',
            },
        },
        editMemberId: user.id,
        selectedMetrics: {
            selectedValues: user.restrictions && user.restrictions.metric ? [...user.restrictions.metric.values] : [],
        },
        screenSelectionForm: {
            selectedScreens: [],
        },
        dimensionFilters: {
            app: user.restrictions && user.restrictions.app ? [...user.restrictions.app.values] : [],
            base_source: user.restrictions && user.restrictions.source ? [...user.restrictions.source.values] : [],
        },
    };
};

export const isOperatorToAddFuture = operator => operator === NOT_IN_OPERATOR;

const deserializeRoleRowPermission = (rowPermission, allValues) => {
    // "Add future" works by negating the filter, and filtering out the non-selected values from the screen
    const addFuture = rowPermission && isOperatorToAddFuture(rowPermission.operator);
    return {
        values: !rowPermission ? [] : addFuture ? difference(allValues, rowPermission.values) : rowPermission.values,
        addFuture,
    };
};

// This method is called when a specific user's permissions are edited
// and it's transforming the user's role/features as they're stored in the DB
// into the state the screen can work with
export const getStateFromUserDetails = (user, data) => {
    if (!user.role) {
        return getStateFromUserDetailsOld(user);
    }
    const { id, name, role } = user;
    const {
        type,
        description,
        row,
        column,
        features,
        section_permissions: sectionPermissions,
        data_restrictions: dataRestrictions,
    } = role;
    // Iterate data.permissionDimensions which is fed from the org's permission_dimensions setting
    // and fill values from the user's row-level permissions, if exist
    const dimensionFilters = {};
    Object.values(data.permissionDimensions).forEach(permissionDimension => {
        const rowPermission = row.find(rp => rp.dimension === permissionDimension.name);
        const allValues = permissionDimension.values.map(x => x.name);

        // If the user has restrictions on this dimension, we deserialize them
        if (rowPermission) {
            dimensionFilters[permissionDimension.name] = deserializeRoleRowPermission(rowPermission, allValues);
        }

        // If the user doesn't have restrictions on this dimension (happens when we add a dimension into the org's
        // permission_dimensions setting, after the user was created) - we grant access to all values
        else {
            dimensionFilters[permissionDimension.name] = { values: allValues, addFuture: true };
        }
    });

    // Handle SDK event restrictions from data_restrictions
    const dataRestrictionsState = {
        sdk_event: {
            values: [],
            addFuture: false,
        },
    };

    if (dataRestrictions) {
        const sdkEventRestriction = dataRestrictions.find(r => r.restriction_type === 'sdk_event');
        if (sdkEventRestriction) {
            const addFuture = sdkEventRestriction.operator === NOT_IN_OPERATOR;
            const restrictionValues = sdkEventRestriction.values || [];
            const allSdkEventNames = data.sdkEventNames || [];

            // For not_in operator: values are the excluded events, we need the selected events (all - excluded)
            // For in operator: values are the selected events directly
            const selectedValues = addFuture ? difference(allSdkEventNames, restrictionValues) : restrictionValues;

            dataRestrictionsState.sdk_event = {
                values: selectedValues,
                addFuture,
            };
        }
    }

    const state = {
        roleSelectionForm: {
            role: type,
        },
        userRegistrationForm: {
            firstName: {
                value: user.first_name,
            },
            lastName: {
                value: user.last_name,
            },
            email: {
                value: user.email,
            },
            description: {
                value: description,
            },
        },
        agencySelectionForm: { id, name },
        editMemberId: id,
        selectedMetrics: deserializeRoleRowPermission(column, data.metrics),
        screenSelectionForm: {
            selectedScreens: features.access_new_features ? difference(data.screens, features.values) : features.values,
            addFuture: features.access_new_features,
        },
        dimensionFilters,
        dataRestrictions: dataRestrictionsState,
        sectionPermissions,
    };

    return state;
};

export const getUniqueApps = appSites =>
    Array.from(appSites.reduce((map, { app }) => map.set(app.name, app), new Map()).values());

export const getSortedMembers = (members, { name, direction }) => {
    if (!direction) return members;
    return members.toSorted((a, b) => {
        const isAscending = direction === 'down';
        switch (name) {
            case 'name':
                return sortAlphabeticallyLowecase(a.searchText, b.searchText, !isAscending);
            case 'roleText':
                return sortAlphabeticallyLowecase(a.roleText, b.roleText, !isAscending);
            case 'modified': {
                const firstDate = new Date(a.role.modified);
                const secondDate = new Date(b.role.modified);
                return sortDate(firstDate, secondDate, isAscending);
            }
            default:
                return 0;
        }
    });
};

export const formatMetricName = field =>
    field.display_name && field.tag ? `${field.display_name} (${field.tag})` : field.display_name;

export const filterFieldsBySearch = (fields, search, translate) => {
    const searchLower = (search || '').toLowerCase().trim();
    if (!searchLower) return fields;

    return fields
        .map(field => {
            const titleMatch = (translate(field.label) || '').toLowerCase().includes(searchLower);
            if (titleMatch) return field;

            const filteredValues = field.values.filter(value =>
                (formatMetricName(value) || '').toLowerCase().includes(searchLower)
            );
            if (filteredValues.length > 0) return { ...field, values: filteredValues };
            return null;
        })
        .filter(Boolean);
};

export const calculateRemovedItems = (initialPermissionSnapshot, dimensionName, allItemIds, currentSelectedValues) => {
    if (!initialPermissionSnapshot) return [];
    const initialRow = initialPermissionSnapshot.role?.row?.find(r => r.dimension === dimensionName);
    if (!initialRow) return [];

    let initialSelected = [];
    if (initialRow.operator === IN_OPERATOR) {
        initialSelected = initialRow.values || [];
    } else if (initialRow.operator === NOT_IN_OPERATOR) {
        const notSelected = initialRow.values || [];
        initialSelected = allItemIds.filter(id => !notSelected.includes(id));
    }

    return initialSelected.filter(item => !currentSelectedValues.includes(item));
};

export const isPartialSelection = (selected, total) => selected > 0 && selected < total;

export const distributeIntoColumns = (items, columnsCount, getCount) => {
    const totalCount = items.reduce((sum, item) => sum + getCount(item), 0);
    const targetPerColumn = totalCount / columnsCount;
    const columns = Array.from({ length: columnsCount }, () => []);
    let columnIndex = 0;
    let currentTotal = 0;

    items.forEach(item => {
        if (columnIndex < columnsCount - 1 && currentTotal >= targetPerColumn) {
            columnIndex++;
            currentTotal = 0;
        }
        columns[columnIndex].push(item);
        currentTotal += getCount(item);
    });

    return columns;
};
